import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send } from "react-feather";
import toast from "react-hot-toast";
import { useSubmitFeedbackMutation } from "../../features/feedback/feedbackApi";

const NAME_STORAGE_KEY = "texasalarms-feedback-reporter-name";

// In-app feedback (handoff §14, testing-phase only) - a persistent
// chat-bubble style widget, always available bottom-right on every
// authenticated page (mounted once in AppLayout.jsx, so it survives route
// changes rather than being remounted per-page). Deliberately just two
// fields (message, reporter name) - pageContext is captured automatically
// from the current route, never shown as a field. The reporter name is
// remembered across submits (and reloads, via localStorage) since the same
// person is often reporting several things in one session and shared
// logins at a branch mean the account alone doesn't identify who typed it.
const FeedbackWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [reporterName, setReporterName] = useState(
    () => localStorage.getItem(NAME_STORAGE_KEY) || ""
  );
  const [message, setMessage] = useState("");
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();

  const handleNameChange = (e) => {
    setReporterName(e.target.value);
    localStorage.setItem(NAME_STORAGE_KEY, e.target.value);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Enter a message before sending");
      return;
    }
    try {
      await submitFeedback({
        reporterName: reporterName.trim() || null,
        pageContext: location.pathname,
        message: message.trim(),
      }).unwrap();
      toast.success("Thanks - your feedback has been recorded");
      setMessage("");
    } catch (error) {
      toast.error(error?.message || "Could not send feedback");
    }
  };

  return (
    <div style={{ position: "fixed", right: 20, bottom: 20, zIndex: 1050 }}>
      {open && (
        <div
          className="card shadow"
          style={{ width: 320, marginBottom: 12 }}
        >
          <div className="card-header d-flex justify-content-between align-items-center bg-primary text-white">
            <span>Report an Issue</span>
            <button
              type="button"
              className="btn btn-link text-white p-0"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="card-body">
            <div className="mb-2">
              <label className="form-label mb-1">Your Name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={reporterName}
                onChange={handleNameChange}
                placeholder="e.g. Jane (Mombasa front desk)"
              />
            </div>
            <div className="mb-2">
              <label className="form-label mb-1">Remarks</label>
              <textarea
                className="form-control form-control-sm"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What went wrong, or what's confusing?"
              />
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              <Send size={14} />
              {isLoading ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary rounded-circle shadow d-flex align-items-center justify-content-center ms-auto"
        style={{ width: 56, height: 56 }}
        onClick={() => setOpen((v) => !v)}
        title="Report an issue"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
};

export default FeedbackWidget;
